// LateralEdgeFromHeight_SquareRegularPyramidTask.js
// Zadanie 4: Ostrosłup prawidłowy czworokątny.
// Dane: krawędź podstawy a, wysokość H.
// Szukane: długość krawędzi bocznej L.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import SqrtInput from "../../../../components/inputs/SqrtInput";

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const nearly = (x, y, eps = 0.01) => Math.abs(x - y) <= eps;

function round2(x) {
  return Math.round(x * 100) / 100;
}

// normalizacja √ → latex
function rawToLatex(raw) {
  const s = String(raw ?? "")
    .trim()
    .replace(/\s+/g, "")
    .replace(/,/g, ".")
    .replace(/[−–]/g, "-");

  return s
    .replace(/([+\-]?\d*)√(\d+)/g, (m, a, b) => {
      const coeff = a === "+" ? "" : a;
      return `${coeff}\\sqrt{${b}}`;
    })
    .replace(/√(\d+)/g, "\\sqrt{$1}");
}

// upraszcza wynik do jednego pierwiastka
function exactLatex(a, H) {
  // L^2 = H^2 + a^2/2 = (2H^2 + a^2)/2
  const num = 2 * H * H + a * a;

  // √(num/2)
  if (num % 2 === 0) {
    return `\\sqrt{${num / 2}}`;
  }
  return `\\sqrt{\\dfrac{${num}}{2}}`;
}

export function generateTask() {
  const a = pick([4, 6, 8, 10, 12]);
  const H = pick([3, 4, 5, 6]);

  const r = (a * Math.sqrt(2)) / 2;
  const L = Math.sqrt(H * H + r * r);

  return {
    type: "LateralEdgeFromHeight_SquareRegularPyramidTask",
    data: { a, H, r, L },
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ marginBottom: 12 }}>
      Ostrosłup <b>prawidłowy czworokątny</b> ma krawędź podstawy{" "}
      <InlineMath math={`{a=${data.a}}`} /> oraz wysokość{" "}
      <InlineMath math={`{H=${data.H}}`} />. Oblicz długość krawędzi bocznej{" "}
      <InlineMath math={`{L}`} />.
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  return (
    <div>
      <div style={{ marginBottom: 6 }}>
        <InlineMath math={`{L =}`} />
      </div>
      <SqrtInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        label="L"
        allowCubeRoot={false}
        onlyCubeRoot={false}
      />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;
  const parsed = userAnswer.parsed;
  if (typeof parsed !== "number" || !Number.isFinite(parsed)) return false;
  return nearly(parsed, data.L, 0.01);
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || !userAnswer.raw) return <i>(brak odpowiedzi)</i>;
  const latex = rawToLatex(userAnswer.raw);
  return <InlineMath math={`{L = ${latex}}`} />;
}

export function renderAnswer({ data }) {
  return <InlineMath math={`{L = ${exactLatex(data.a, data.H)}}`} />;
}

export function renderExplanation({ data }) {
  const { a, H, r, L } = data;
  const approx = round2(L);

  return (
    <div style={{ display: "grid", rowGap: 10 }}>
      <p>
        <strong>Krok 1.</strong> Podstawa ostrosłupa jest kwadratem, więc jego
        przekątna wynosi:
      </p>
      <BlockMath math={`{d = a\\sqrt{2} = ${a}\\sqrt{2}}`} />

      <p>
        <strong>Krok 2.</strong> Odległość środka podstawy od wierzchołka
        kwadratu:
      </p>
      <BlockMath math={`{r = \\tfrac{d}{2} = \\tfrac{${a}\\sqrt{2}}{2}}`} />

      <p>
        <strong>Krok 3.</strong> Krawędź boczna <InlineMath math={`{L}`} /> jest
        przeciwprostokątną trójkąta prostokątnego:
      </p>
      <BlockMath math={`{L^2 = H^2 + r^2}`} />

      <p>
        <strong>Krok 4.</strong> Podstawiamy dane:
      </p>
      <BlockMath
        math={`{L = \\sqrt{${H}^2 + \\left(\\tfrac{${a}\\sqrt{2}}{2}\\right)^2}}`}
      />

      <p>
        <strong>Wynik dokładny:</strong>{" "}
        <InlineMath math={`{L = ${exactLatex(a, H)}}`} />
      </p>

      <p style={{ opacity: 0.85 }}>
        <strong>Wartość przybliżona:</strong>{" "}
        <InlineMath math={`{${approx}}`} />
      </p>
    </div>
  );
}
